import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, Upload, CheckCircle2, AlertTriangle, ChevronRight, ChevronLeft, Mail, ArrowRight, Info, Heart, HelpCircle, Sparkles, ThumbsUp, Eye, Wrench, ShieldCheck, Users, Trophy, Dumbbell, ClipboardCheck, Building2, MapPin, Phone, FileText, RotateCcw, X, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ThemedButton } from '@/components/ThemedButton';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = {
  WELCOME: 0,
  CHOOSE_TRACK: 1,
  UPLOAD_COI: 2,
  UPLOAD_REQ: 3,
  ASSESSMENT: 4,
  PROCESSING: 5,
  RESULTS: 6
};

/* ─── Shared UI Components ───────────────────────────── */

const HighlightIcon = ({ icon }) => {
  if (icon === 'check') return <ThumbsUp className="h-5 w-5 text-[#065F46] flex-shrink-0" />;
  if (icon === 'tip') return <Sparkles className="h-5 w-5 text-[#7B6FE8] flex-shrink-0" />;
  return <Info className="h-5 w-5 text-[#0369A1] flex-shrink-0" />;
};

const HighlightCard = ({ highlight }) => {
  const bgMap = { good: 'bg-[#ECFDF5] border-[#A7F3D0]', fyi: 'bg-[#F0F9FF] border-[#BAE6FD]', action: 'bg-[#EEF0FF] border-[#C4BFFF]' };
  const bg = bgMap[highlight.priority] || bgMap.fyi;
  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border ${bg}`} data-testid="highlight-card">
      <HighlightIcon icon={highlight.icon} />
      <div>
        <p className="font-semibold text-sm text-[#1A1A1A]">{highlight.title}</p>
        <p className="text-sm text-[#555] mt-1 leading-relaxed">{highlight.detail}</p>
      </div>
    </div>
  );
};

const ProtectionBadge = ({ level }) => {
  const config = {
    'Well Protected': { bg: 'bg-[#ECFDF5]', border: 'border-[#A7F3D0]', text: 'text-[#065F46]' },
    'Partially Protected': { bg: 'bg-[#FFFBEB]', border: 'border-[#FDE68A]', text: 'text-[#92400E]' },
    'Exposed': { bg: 'bg-[#EEF0FF]', border: 'border-[#C4BFFF]', text: 'text-[#7B6FE8]' },
  };
  const c = config[level] || config['Partially Protected'];
  const label = level === 'Exposed' ? "Let's Talk" : level;
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold border ${c.bg} ${c.border} ${c.text}`}>{label}</span>;
};

const StatusBadge = ({ status }) => {
  const config = {
    MET: { bg: 'bg-[#ECFDF5]', border: 'border-[#A7F3D0]', text: 'text-[#065F46]', label: 'Looks Good' },
    GAP: { bg: 'bg-[#FFFBEB]', border: 'border-[#FDE68A]', text: 'text-[#92400E]', label: 'Worth Checking' },
    NOT_SHOWN: { bg: 'bg-[#EEF0FF]', border: 'border-[#C4BFFF]', text: 'text-[#7B6FE8]', label: 'We Can Help' },
    Protected: { bg: 'bg-[#ECFDF5]', border: 'border-[#A7F3D0]', text: 'text-[#065F46]', label: 'Protected' },
    'Partially Protected': { bg: 'bg-[#FFFBEB]', border: 'border-[#FDE68A]', text: 'text-[#92400E]', label: 'Partially Protected' },
    Exposed: { bg: 'bg-[#EEF0FF]', border: 'border-[#C4BFFF]', text: 'text-[#7B6FE8]', label: "Let's Talk" },
  };
  const c = config[status] || config.NOT_SHOWN;
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold border ${c.bg} ${c.border} ${c.text}`}>{c.label}</span>;
};

const FileDropZone = ({ onFileSelect, file, accept, label, description }) => {
  const [dragOver, setDragOver] = useState(false);
  const handleDrop = useCallback((e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) onFileSelect(e.dataTransfer.files[0]); }, [onFileSelect]);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${dragOver ? 'border-[#7B6FE8] bg-[#EEF0FF]' : file ? 'border-[#A7F3D0] bg-[#ECFDF5]' : 'border-[#E5E7EB] hover:border-[#7B6FE8]/50 hover:bg-[#EEF0FF]/30'}`}
      onClick={() => document.getElementById(`file-${label}`)?.click()}
      data-testid="file-drop-zone"
    >
      <input id={`file-${label}`} type="file" accept={accept} className="hidden" onChange={(e) => e.target.files?.[0] && onFileSelect(e.target.files[0])} />
      {file ? (
        <div className="flex flex-col items-center gap-3">
          <CheckCircle2 className="h-10 w-10 text-[#065F46]" />
          <p className="font-semibold text-[#1A1A1A]">{file.name}</p>
          <p className="text-sm text-[#6B7280]">{(file.size / 1024).toFixed(0)} KB</p>
          <p className="text-xs text-[#7B6FE8]">Click to change file</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <Upload className="h-10 w-10 text-[#7B6FE8]" />
          <p className="font-semibold text-[#1A1A1A]">{label}</p>
          <p className="text-sm text-[#6B7280]">{description}</p>
          <p className="text-xs text-[#9CA3AF]">PDF, PNG, JPG — Max 10MB</p>
        </div>
      )}
    </div>
  );
};

const ScoreRing = ({ score, size = 140, label }) => {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#065F46' : score >= 60 ? '#92400E' : '#7B6FE8';
  const bg = score >= 80 ? '#ECFDF5' : score >= 60 ? '#FFFBEB' : '#EEF0FF';
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill={bg} stroke="#E5E7EB" strokeWidth="8" />
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} className="transition-all duration-1000" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-black" style={{ color, fontFamily: "'Barlow Condensed', sans-serif" }}>{score}</span>
          <span className="text-xs text-[#6B7280]">/ 100</span>
        </div>
      </div>
      {label && <p className="text-sm font-semibold mt-2" style={{ color }}>{label}</p>}
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────── */

const RA_STORAGE_KEY = 'ph_draft_assessment';

const RiskAssessment = () => {
  const navigate = useNavigate();

  // Check for saved draft
  const savedRaw = typeof window !== 'undefined' ? localStorage.getItem(RA_STORAGE_KEY) : null;
  const saved = savedRaw ? JSON.parse(savedRaw) : null;

  const [showResume, setShowResume] = useState(!!saved && saved.step > STEPS.WELCOME);
  const [step, setStep] = useState(STEPS.WELCOME);
  const [assessmentType, setAssessmentType] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentCategory, setCurrentCategory] = useState(0);
  const [coiFiles, setCoiFiles] = useState([]);
  const [reqFile, setReqFile] = useState(null);
  const [coiFileIds, setCoiFileIds] = useState([]);
  const [reqFileId, setReqFileId] = useState(null);
  const [bgAnalysisStarted, setBgAnalysisStarted] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [email, setEmail] = useState('');
  const [emailSaved, setEmailSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Auto-save assessment progress (non-file state only)
  useEffect(() => {
    if (!showResume && step > STEPS.WELCOME && step < STEPS.RESULTS) {
      localStorage.setItem(RA_STORAGE_KEY, JSON.stringify({
        step,
        assessmentType,
        answers,
        currentCategory,
        coiFileIds,
        reqFileId,
        bgAnalysisStarted,
        savedAt: new Date().toISOString(),
      }));
    }
    if (step === STEPS.RESULTS) {
      localStorage.removeItem(RA_STORAGE_KEY);
    }
  }, [step, assessmentType, answers, currentCategory, coiFileIds, reqFileId, bgAnalysisStarted, showResume]);

  const resumeDraft = useCallback(() => {
    if (saved) {
      setStep(saved.step || STEPS.WELCOME);
      setAssessmentType(saved.assessmentType || null);
      setAnswers(saved.answers || {});
      setCurrentCategory(saved.currentCategory || 0);
      setCoiFileIds(saved.coiFileIds || []);
      setReqFileId(saved.reqFileId || null);
      setBgAnalysisStarted(saved.bgAnalysisStarted || false);
    }
    setShowResume(false);
  }, [saved]);

  const discardDraft = useCallback(() => {
    localStorage.removeItem(RA_STORAGE_KEY);
    setShowResume(false);
  }, []);

  // Group questions by category
  const categories = questions.length > 0 ? [...new Set(questions.map(q => q.category))] : [];
  const questionsInCategory = categories[currentCategory] ? questions.filter(q => q.category === categories[currentCategory]) : [];
  const allAnswered = questions.length > 0 && questions.every(q => answers[q.id] !== undefined);
  const isPoolCategory = categories[currentCategory] === 'Pool Safety';

  useEffect(() => {
    if (assessmentType) {
      axios.get(`${API_URL}/risk-assessment/questions/${assessmentType}`)
        .then(r => setQuestions(r.data.questions))
        .catch(() => toast.error('Failed to load questions'));
    }
  }, [assessmentType]);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/risk-assessment/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  };

  // Upload COI and start background AI analysis immediately
  const handleCoiUploadAndAnalyze = async () => {
    if (coiFiles.length === 0) { setStep(STEPS.UPLOAD_REQ); return; }
    try {
      setUploading(true);
      const newIds = [...coiFileIds];
      for (const file of coiFiles) {
        const result = await uploadFile(file);
        newIds.push(result.id);
        // Kick off background analysis for each file
        axios.post(`${API_URL}/risk-assessment/start-analysis/${result.id}`).catch(() => {});
      }
      setCoiFileIds(newIds);
      setBgAnalysisStarted(true);
      setUploading(false);
      setStep(STEPS.UPLOAD_REQ);
    } catch (error) {
      toast.error('Upload failed. Please try again.');
      setUploading(false);
    }
  };

  // Upload requirements file (no background analysis needed for this)
  const handleReqUploadAndContinue = async () => {
    if (reqFile && !reqFileId) {
      try {
        setUploading(true);
        const result = await uploadFile(reqFile);
        setReqFileId(result.id);
        setUploading(false);
      } catch {
        setUploading(false);
      }
    }
    setStep(STEPS.ASSESSMENT);
  };

  const handleAnalyze = async () => {
    setStep(STEPS.PROCESSING);
    try {
      const response = await axios.post(`${API_URL}/risk-assessment/analyze`, {
        coi_file_ids: coiFileIds,
        requirements_file_id: reqFileId,
        questionnaire_answers: answers,
        assessment_type: assessmentType,
        terms_accepted: true
      });
      setAnalysis(response.data);
      setStep(STEPS.RESULTS);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
      setStep(STEPS.ASSESSMENT);
    }
  };

  const handleSaveEmail = async () => {
    if (!email || !analysis?.id) return;
    try {
      const res = await axios.post(`${API_URL}/risk-assessment/${analysis.id}/save-contact`, { email });
      setEmailSaved(true);
      if (res.data?.email_sent) {
        toast.success("Report sent! Check your inbox.");
      } else {
        toast.success("Email saved — we'll follow up soon!");
      }
    } catch { toast.error('Failed to send report'); }
  };

  const getRiskLabel = (score) => {
    if (score >= 80) return { color: '#065F46', bg: '#ECFDF5', border: '#A7F3D0', label: 'Strong Foundation' };
    if (score >= 60) return { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', label: 'Good Start — A Few Gaps' };
    return { color: '#7B6FE8', bg: '#EEF0FF', border: '#C4BFFF', label: 'Needs Attention — We Can Help' };
  };

  const coi = analysis?.coi_analysis || {};
  const highlights = coi.highlights || [];
  const riskAssessment = coi.risk_assessment || {};
  const topRisks = riskAssessment.top_risks || [];
  const mitigationTips = riskAssessment.mitigation_tips || [];
  const questionResults = analysis?.question_results || [];
  const safetyScore = analysis?.safety_score || {};
  const insuranceScore = analysis?.insurance_score;

  const getQuotePath = () => {
    const base = assessmentType === 'fitness' ? '/quote/fitness' : '/quote/sports';
    return analysis?.id ? `${base}?risk_assessment_id=${analysis.id}` : base;
  };

  // Group question results by category for display
  const resultCategories = questionResults.length > 0 ? [...new Set(questionResults.map(q => q.category))] : [];

  const answeredCount = Object.keys(answers).length;
  const progressPct = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Helmet>
        <title>Free Insurance Risk Assessment | Players Health</title>
        <meta name="description" content="Upload your Certificate of Insurance and get a free AI-powered risk assessment. Identify coverage gaps and get personalized recommendations for your sports or fitness organization." />
        <link rel="canonical" href="https://www.playershealth.com/risk-assessment" />
      </Helmet>
      <nav className="bg-white/95 backdrop-blur-md border-b border-[#E5E7EB] sticky top-0 z-50">
        <div className="max-w-[1140px] mx-auto px-6 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo-dark.png" alt="Players Health" className="h-9" />
          </div>
          <div className="flex items-center gap-3">
            {step > STEPS.WELCOME && step < STEPS.RESULTS && (
              <span className="text-xs text-[#6B7280] hidden sm:inline-flex items-center gap-1"><Save className="h-3 w-3" />Auto-saved</span>
            )}
            <span className="text-xs font-bold text-[#7B6FE8] uppercase tracking-widest bg-[#EEF0FF] px-3 py-1 rounded-md" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Risk Assessment</span>
          </div>
        </div>
        <div className="h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12">

        {/* ═══ RESUME DRAFT PROMPT ═══ */}
        {showResume && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="max-w-md w-full bg-white border border-[#E5E7EB] rounded-xl shadow-lg p-8 relative overflow-hidden" data-testid="resume-assessment-dialog">
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
              <div className="flex items-center gap-3 mb-4">
                <RotateCcw className="h-6 w-6 text-[#7B6FE8]" />
                <h2 className="text-xl font-black text-[#1A1A1A] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Resume Your Assessment?</h2>
              </div>
              <p className="text-sm text-[#6B7280] mb-6">We found a saved assessment from your previous session. Would you like to pick up where you left off?</p>
              <div className="flex gap-3">
                <Button onClick={resumeDraft} data-testid="resume-assessment-btn" className="flex-1 bg-[#7B6FE8] text-white hover:bg-[#6B5CE7] font-semibold">
                  <RotateCcw className="mr-2 h-4 w-4" /> Resume
                </Button>
                <Button onClick={discardDraft} variant="outline" data-testid="discard-assessment-btn" className="flex-1 border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6]">
                  <X className="mr-2 h-4 w-4" /> Start Fresh
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ WELCOME ═══ */}
        {!showResume && step === STEPS.WELCOME && (
          <div data-testid="risk-assessment-welcome">
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#6B5CE7]/10 to-[#4ECDC4]/10 mb-8">
                <Shield className="h-10 w-10 text-[#7B6FE8]" />
              </div>
              <h1 className="font-black text-[#1A1A1A] uppercase mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(32px, 5vw, 52px)' }}>
                Risk<br />
                <span className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] bg-clip-text text-transparent">Assessment</span>
              </h1>
              <p className="text-[#555] text-lg max-w-xl mx-auto mb-4 leading-relaxed">
                At Players Health, our mission is to create the <strong>safest and most accessible environment</strong> for every athlete to play the sport they love.
              </p>
              <p className="text-[#555] max-w-lg mx-auto mb-2 leading-relaxed">
                All we do is sports and fitness — we've seen it all. We are athletes, admins, and parents who love what sports bring to the world.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-10 text-left max-w-xl mx-auto">
              <div className="bg-[#F7F8FA] rounded-xl p-5 border border-[#E5E7EB]">
                <Eye className="h-6 w-6 text-[#7B6FE8] mb-3" />
                <h4 className="font-semibold text-sm text-[#1A1A1A] mb-1">Visibility</h4>
                <p className="text-xs text-[#6B7280]">See the risks your organization actually faces — clearly and honestly</p>
              </div>
              <div className="bg-[#F7F8FA] rounded-xl p-5 border border-[#E5E7EB]">
                <Wrench className="h-6 w-6 text-[#4ECDC4] mb-3" />
                <h4 className="font-semibold text-sm text-[#1A1A1A] mb-1">Tools & Resources</h4>
                <p className="text-xs text-[#6B7280]">Practical steps to mitigate risk before it becomes a problem</p>
              </div>
              <div className="bg-[#F7F8FA] rounded-xl p-5 border border-[#E5E7EB]">
                <ShieldCheck className="h-6 w-6 text-[#7B6FE8] mb-3" />
                <h4 className="font-semibold text-sm text-[#1A1A1A] mb-1">Insurance</h4>
                <p className="text-xs text-[#6B7280]">Transfer the risk you can't eliminate with the right coverage</p>
              </div>
            </div>

            <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-xl p-6 mb-10 max-w-xl mx-auto">
              <p className="text-sm text-[#0369A1] text-center leading-relaxed">
                <strong>How it works:</strong> Answer a few questions about your organization's safety practices, then upload your insurance certificate. We'll give you a comprehensive risk assessment with a personalized score and actionable recommendations.
              </p>
            </div>

            <div className="text-center">
              <ThemedButton
                onClick={() => setStep(STEPS.CHOOSE_TRACK)}
                data-testid="start-assessment-btn"
                variant="gradient" size="lg"
              >
                Start My Risk Assessment <ChevronRight className="ml-1 h-4 w-4" />
              </ThemedButton>
              <p className="text-xs text-[#9CA3AF] mt-8 max-w-md mx-auto">
                Takes about 5 minutes. This is a risk assessment tool — not insurance advice. We're here to help you understand where you stand.
              </p>
            </div>
          </div>
        )}

        {/* ═══ CHOOSE TRACK ═══ */}
        {step === STEPS.CHOOSE_TRACK && (
          <div data-testid="choose-track-step">
            <button onClick={() => setStep(STEPS.WELCOME)} className="flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#7B6FE8] mb-6"><ChevronLeft className="h-4 w-4" /> Back</button>
            <h2 className="font-black text-[#1A1A1A] uppercase mb-2 text-2xl text-center" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              What type of organization are you?
            </h2>
            <p className="text-[#6B7280] text-center mb-8">We'll tailor the assessment to the specific risks your type of organization faces.</p>

            <div className="grid sm:grid-cols-2 gap-6 max-w-lg mx-auto">
              <button
                onClick={() => { setAssessmentType('sports'); setStep(STEPS.UPLOAD_COI); }}
                data-testid="track-sports-btn"
                className="group p-8 rounded-2xl border-2 border-[#E5E7EB] hover:border-[#7B6FE8] bg-white hover:bg-[#EEF0FF]/30 transition-all text-center"
              >
                <Trophy className="h-12 w-12 text-[#7B6FE8] mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-black text-[#1A1A1A] uppercase text-lg mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Youth & Amateur Sports</h3>
                <p className="text-xs text-[#6B7280]">Leagues, clubs, camps, tournaments, and governing bodies</p>
              </button>
              <button
                onClick={() => { setAssessmentType('fitness'); setStep(STEPS.UPLOAD_COI); }}
                data-testid="track-fitness-btn"
                className="group p-8 rounded-2xl border-2 border-[#E5E7EB] hover:border-[#4ECDC4] bg-white hover:bg-[#ECFDF5]/30 transition-all text-center"
              >
                <Dumbbell className="h-12 w-12 text-[#4ECDC4] mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-black text-[#1A1A1A] uppercase text-lg mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Boutique Fitness</h3>
                <p className="text-xs text-[#6B7280]">Gyms, studios, personal training, CrossFit, yoga, and wellness</p>
              </button>
            </div>
          </div>
        )}

        {/* ═══ ASSESSMENT QUESTIONS ═══ */}
        {step === STEPS.ASSESSMENT && categories.length > 0 && (
          <div data-testid="assessment-questions-step">
            <button onClick={() => currentCategory > 0 ? setCurrentCategory(c => c - 1) : setStep(STEPS.UPLOAD_REQ)} className="flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#7B6FE8] mb-6">
              <ChevronLeft className="h-4 w-4" /> {currentCategory > 0 ? 'Previous Section' : 'Back'}
            </button>

            {/* Progress */}
            <div className="mb-6">
              {/* Background analysis runs silently */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-[#7B6FE8] uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  Section {currentCategory + 1} of {categories.length}
                </span>
                <span className="text-xs text-[#6B7280]">{answeredCount} of {questions.length} answered</span>
              </div>
              <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }}></div>
              </div>
            </div>

            {/* Category Header */}
            <div className="flex items-center gap-3 mb-6">
              <ClipboardCheck className="h-6 w-6 text-[#7B6FE8]" />
              <h2 className="font-black text-[#1A1A1A] uppercase text-xl" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {categories[currentCategory]}
              </h2>
            </div>

            {/* Questions */}
            <div className="space-y-4">
              {questionsInCategory.map((q) => (
                <Card key={q.id} className={`border transition-all ${answers[q.id] !== undefined ? (answers[q.id] === 'na' ? 'border-[#E5E7EB] bg-[#F7F8FA]' : 'border-[#A7F3D0] bg-[#FAFFFE]') : 'border-[#E5E7EB]'}`} data-testid={`question-card-${q.id}`}>
                  <CardContent className="pt-5 pb-5">
                    <p className="text-sm font-semibold text-[#1A1A1A] mb-1 leading-relaxed">{q.question}</p>
                    <p className="text-xs text-[#6B7280] mb-4 leading-relaxed">{q.explanation}</p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setAnswers(a => ({ ...a, [q.id]: true }))}
                        data-testid={`answer-yes-${q.id}`}
                        className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${answers[q.id] === true ? 'bg-[#065F46] text-white' : 'bg-[#F7F8FA] text-[#1A1A1A] border border-[#E5E7EB] hover:border-[#065F46]'}`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setAnswers(a => ({ ...a, [q.id]: false }))}
                        data-testid={`answer-no-${q.id}`}
                        className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${answers[q.id] === false ? 'bg-[#7B6FE8] text-white' : 'bg-[#F7F8FA] text-[#1A1A1A] border border-[#E5E7EB] hover:border-[#7B6FE8]'}`}
                      >
                        No
                      </button>
                      {isPoolCategory && (
                        <button
                          onClick={() => setAnswers(a => ({ ...a, [q.id]: 'na' }))}
                          data-testid={`answer-na-${q.id}`}
                          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${answers[q.id] === 'na' ? 'bg-[#6B7280] text-white' : 'bg-[#F7F8FA] text-[#1A1A1A] border border-[#E5E7EB] hover:border-[#6B7280]'}`}
                        >
                          N/A
                        </button>
                      )}
                      {answers[q.id] !== undefined && answers[q.id] !== 'na' && (
                        <span className="text-xs text-[#6B7280] ml-2">
                          {answers[q.id] ? `+${q.points} pts` : '0 pts'}
                        </span>
                      )}
                      {answers[q.id] === 'na' && (
                        <span className="text-xs text-[#6B7280] ml-2 italic">Not applicable</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-8">
              {currentCategory > 0 && (
                <Button onClick={() => setCurrentCategory(c => c - 1)} variant="ghost" className="text-[#6B7280] hover:text-[#7B6FE8]">
                  <ChevronLeft className="mr-1 h-4 w-4" /> Previous
                </Button>
              )}
              <div className="ml-auto">
                {currentCategory < categories.length - 1 ? (
                  <ThemedButton onClick={() => setCurrentCategory(c => c + 1)} data-testid="next-category-btn" variant="gradient" size="md">
                    Next Section <ChevronRight className="ml-1 h-4 w-4" />
                  </ThemedButton>
                ) : (
                  <ThemedButton onClick={handleAnalyze} disabled={!allAnswered} data-testid="finish-assessment-btn" variant="gradient" size="md">
                    Generate My Report <ArrowRight className="ml-1 h-4 w-4" />
                  </ThemedButton>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═══ UPLOAD COI ═══ */}
        {step === STEPS.UPLOAD_COI && (
          <div data-testid="upload-coi-step">
            <button onClick={() => setStep(STEPS.CHOOSE_TRACK)} className="flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#7B6FE8] mb-6"><ChevronLeft className="h-4 w-4" /> Back</button>
            <h2 className="font-black text-[#1A1A1A] uppercase mb-2 text-2xl" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Upload Your Certificate{coiFiles.length > 0 ? 's' : ''}</h2>
            <p className="text-[#6B7280] mb-3">Upload your Certificate(s) of Insurance — you can add multiple if you have more than one.</p>
            <div className="flex items-start gap-2 p-3 bg-[#F0F9FF] border border-[#BAE6FD] rounded-lg mb-6">
              <HelpCircle className="h-4 w-4 text-[#0369A1] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-[#0369A1]">Not sure where to find it? Check your email for something from your insurance company — it's usually attached as a PDF. If you can't find it, your broker can send you a fresh copy.</p>
            </div>

            {/* Show already-added files */}
            {coiFiles.length > 0 && (
              <div className="space-y-2 mb-4">
                {coiFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-[#065F46]" />
                      <span className="text-sm text-[#1A1A1A]">{f.name}</span>
                      <span className="text-xs text-[#6B7280]">({(f.size / 1024).toFixed(0)} KB)</span>
                    </div>
                    <button onClick={() => setCoiFiles(files => files.filter((_, idx) => idx !== i))} className="text-xs text-[#991B1B] hover:underline">Remove</button>
                  </div>
                ))}
              </div>
            )}

            <FileDropZone
              onFileSelect={(f) => setCoiFiles(files => [...files, f])}
              file={null}
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              label={coiFiles.length > 0 ? "Add another certificate" : "Drop your certificate here"}
              description={coiFiles.length > 0 ? "Have more than one COI? Drop it here" : "The PDF or image your agent sent you"}
            />
            {uploading && <p className="text-sm text-[#7B6FE8] mt-3 text-center">Uploading {coiFiles.length} certificate{coiFiles.length > 1 ? 's' : ''}...</p>}
            <div className="flex justify-between mt-8">
              <Button onClick={() => setStep(STEPS.UPLOAD_REQ)} variant="ghost" className="text-[#6B7280] hover:text-[#7B6FE8]">Skip — I don't have one</Button>
              <ThemedButton onClick={handleCoiUploadAndAnalyze} disabled={coiFiles.length === 0 || uploading} data-testid="coi-next-btn" variant="gradient" size="md">
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </ThemedButton>
            </div>
          </div>
        )}

        {/* ═══ UPLOAD REQUIREMENTS ═══ */}
        {step === STEPS.UPLOAD_REQ && (
          <div data-testid="upload-req-step">
            <button onClick={() => setStep(STEPS.UPLOAD_COI)} className="flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#7B6FE8] mb-6"><ChevronLeft className="h-4 w-4" /> Back</button>
            <h2 className="font-black text-[#1A1A1A] uppercase mb-2 text-2xl" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Got Requirements?
              <span className="text-sm font-normal text-[#9CA3AF] ml-3 normal-case" style={{ fontFamily: "'Inter', sans-serif" }}>(Optional)</span>
            </h2>
            <p className="text-[#6B7280] mb-3">If a venue, school, or landlord sent you a list of insurance requirements, upload it here and we'll check your certificate against it.</p>
            <FileDropZone onFileSelect={setReqFile} file={reqFile} accept=".pdf,.png,.jpg,.jpeg,.webp" label="Drop requirements document here" description="The document listing what coverage you need" />
            {uploading && <p className="text-sm text-[#7B6FE8] mt-3 text-center">Uploading...</p>}
            {/* Background analysis runs silently */}
            <div className="flex justify-between mt-8">
              <Button onClick={() => { setStep(STEPS.ASSESSMENT); }} variant="ghost" className="text-[#6B7280] hover:text-[#7B6FE8]">Skip — I don't have one</Button>
              <ThemedButton onClick={handleReqUploadAndContinue} disabled={uploading} data-testid="req-next-btn" variant="gradient" size="md">
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </ThemedButton>
            </div>
          </div>
        )}

        {/* ═══ PROCESSING ═══ */}
        {step === STEPS.PROCESSING && (
          <div className="text-center py-20" data-testid="processing-step">
            <div className="relative inline-block mb-8">
              <div className="w-20 h-20 rounded-full border-4 border-[#EEF0FF] border-t-[#7B6FE8] animate-spin"></div>
              <Shield className="h-8 w-8 text-[#7B6FE8] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <h2 className="font-black text-[#1A1A1A] uppercase mb-3 text-2xl" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {uploading ? 'Uploading Your Documents...' : 'Building Your Risk Assessment'}
            </h2>
            <p className="text-[#6B7280] max-w-md mx-auto">
              {uploading ? 'Securely uploading your files...' : bgAnalysisStarted ? "Combining your questionnaire results with your certificate analysis..." : "We're analyzing your responses and building your personalized report..."}
            </p>
          </div>
        )}

        {/* ═══ RESULTS ═══ */}
        {step === STEPS.RESULTS && analysis && (
          <div data-testid="results-step" className="space-y-8">
            {/* Header */}
            <div className="text-center">
              <h2 className="font-black text-[#1A1A1A] uppercase text-2xl mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Your Risk Assessment Report
              </h2>
              <p className="text-[#6B7280] text-sm">
                {assessmentType === 'sports' ? 'Youth & Amateur Sports' : 'Boutique Fitness'} Assessment
              </p>
            </div>

            {/* Combined Score */}
            <div className="flex flex-wrap items-center justify-center gap-8" data-testid="score-section">
              <ScoreRing score={analysis.risk_score} size={160} label={getRiskLabel(analysis.risk_score).label} />
              <div className="flex flex-col gap-3 text-left">
                {safetyScore.percentage !== undefined && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#EEF0FF] flex items-center justify-center">
                      <ClipboardCheck className="h-5 w-5 text-[#7B6FE8]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#6B7280]">Safety Practices</p>
                      <p className="font-bold text-[#1A1A1A]">{safetyScore.earned} / {safetyScore.max} pts <span className="text-xs font-normal text-[#6B7280]">({safetyScore.percentage}%)</span></p>
                    </div>
                  </div>
                )}
                {insuranceScore !== null && insuranceScore !== undefined && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#ECFDF5] flex items-center justify-center">
                      <ShieldCheck className="h-5 w-5 text-[#065F46]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#6B7280]">Insurance Coverage</p>
                      <p className="font-bold text-[#1A1A1A]">{insuranceScore} / 100</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Organization Details from COI */}
            {coi.organization_details && (coi.organization_details.name || coi.named_insured) && (
              <Card className="border border-[#E5E7EB]" data-testid="org-details-card">
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="h-5 w-5 text-[#7B6FE8]" />
                    <p className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Organization Details</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    <div>
                      <p className="text-xs text-[#6B7280] mb-0.5">Insured Name</p>
                      <p className="text-sm font-medium text-[#1A1A1A]">{coi.organization_details?.name || coi.named_insured}</p>
                    </div>
                    {coi.organization_details?.dba && (
                      <div>
                        <p className="text-xs text-[#6B7280] mb-0.5">DBA</p>
                        <p className="text-sm font-medium text-[#1A1A1A]">{coi.organization_details.dba}</p>
                      </div>
                    )}
                    {coi.organization_details?.address && (
                      <div className="flex items-start gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-[#6B7280] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-[#6B7280] mb-0.5">Address</p>
                          <p className="text-sm text-[#1A1A1A]">{coi.organization_details.address}</p>
                        </div>
                      </div>
                    )}
                    {coi.organization_details?.phone && (
                      <div className="flex items-start gap-1.5">
                        <Phone className="h-3.5 w-3.5 text-[#6B7280] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-[#6B7280] mb-0.5">Phone</p>
                          <p className="text-sm text-[#1A1A1A]">{coi.organization_details.phone}</p>
                        </div>
                      </div>
                    )}
                    {coi.organization_details?.email && (
                      <div className="flex items-start gap-1.5">
                        <Mail className="h-3.5 w-3.5 text-[#6B7280] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-[#6B7280] mb-0.5">Email</p>
                          <p className="text-sm text-[#1A1A1A]">{coi.organization_details.email}</p>
                        </div>
                      </div>
                    )}
                    {coi.organization_details?.description && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-[#6B7280] mb-0.5">Organization Type</p>
                        <p className="text-sm text-[#1A1A1A]">{coi.organization_details.description}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Summary */}
            {coi.summary_for_user && (
              <Card className="bg-[#F7F8FA] border border-[#E5E7EB]">
                <CardContent className="pt-5 pb-5">
                  <div className="flex gap-3">
                    <Heart className="h-5 w-5 text-[#7B6FE8] flex-shrink-0 mt-0.5" />
                    <p className="text-[#1A1A1A] leading-relaxed">{coi.summary_for_user}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Primary CTA - Get a Quote */}
            <Card className="bg-[#232323] border-0 text-white overflow-hidden relative" data-testid="primary-cta-section">
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
              <CardContent className="pt-7 pb-7 text-center">
                <ShieldCheck className="h-8 w-8 text-[#4ECDC4] mx-auto mb-3" />
                <h3 className="font-black uppercase text-xl mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Ready for the Right Coverage?</h3>
                <p className="text-white/70 text-sm mb-5 max-w-md mx-auto leading-relaxed">
                  Based on your assessment, let us find you the right protection at the right price. Your information has been pre-filled to save you time.
                </p>
                <ThemedButton
                  onClick={() => navigate(getQuotePath())}
                  data-testid="primary-get-quote-btn"
                  variant="gradient" size="lg"
                >
                  Get a Personalized Quote <ChevronRight className="ml-1 h-4 w-4" />
                </ThemedButton>
              </CardContent>
            </Card>

            {/* COI Analysis Error or Add More COIs */}
            {analysis.coi_analysis_error && (
              <div className="flex items-start gap-3 p-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-xl" data-testid="coi-error-notice">
                <AlertTriangle className="h-5 w-5 text-[#92400E] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#92400E]">Insurance Review Unavailable</p>
                  <p className="text-sm text-[#555] mt-1">{analysis.coi_analysis_error}</p>
                </div>
              </div>
            )}

            {/* Upload Additional COI on Results Page */}
            <Card className="border border-[#E5E7EB]" data-testid="add-coi-section">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Upload className="h-5 w-5 text-[#7B6FE8]" />
                  <p className="text-sm font-semibold text-[#1A1A1A]">
                    {!analysis.coi_analysis ? 'Upload a Certificate to Complete Your Insurance Review' : 'Have Another Certificate?'}
                  </p>
                </div>
                <p className="text-xs text-[#6B7280] mb-3">
                  {!analysis.coi_analysis
                    ? "Drop your COI here and we'll analyze your insurance coverage to give you a complete picture."
                    : "Organizations often have multiple policies. Upload additional COIs to get a complete picture of your coverage."}
                </p>
                <FileDropZone
                  onFileSelect={async (f) => {
                    try {
                      setUploading(true);
                      const result = await uploadFile(f);
                      await axios.post(`${API_URL}/risk-assessment/start-analysis/${result.id}`);
                      setCoiFileIds(ids => [...ids, result.id]);
                      toast.success('Certificate uploaded! Re-generating your report with the new data...');
                      setUploading(false);
                      // Re-run analysis with all COIs
                      const response = await axios.post(`${API_URL}/risk-assessment/analyze`, {
                        coi_file_ids: [...coiFileIds, result.id],
                        requirements_file_id: reqFileId,
                        questionnaire_answers: answers,
                        assessment_type: assessmentType,
                        terms_accepted: true
                      });
                      setAnalysis(response.data);
                    } catch (error) {
                      toast.error('Upload failed. Please try again.');
                      setUploading(false);
                    }
                  }}
                  file={null}
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  label={uploading ? 'Uploading & analyzing...' : 'Drop a certificate here'}
                  description="PDF or image — we'll analyze it and update your report"
                />
              </CardContent>
            </Card>

            {/* Email Capture */}
            {!emailSaved ? (
              <Card className="bg-[#EEF0FF] border border-[#C4BFFF]">
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-center gap-4 flex-wrap">
                    <Mail className="h-5 w-5 text-[#7B6FE8] flex-shrink-0" />
                    <p className="text-sm text-[#1A1A1A] flex-1 min-w-[200px]"><strong>Want a copy of your report?</strong> We'll email it so you can share with your team.</p>
                    <div className="flex gap-2 flex-1 min-w-[280px]">
                      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" data-testid="email-capture-input" className="border-[#C4BFFF] focus:border-[#7B6FE8] bg-white" />
                      <Button onClick={handleSaveEmail} disabled={!email} data-testid="save-email-btn" className="bg-[#7B6FE8] text-white hover:bg-[#6B5CE7] shrink-0">Send</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="flex items-center justify-between gap-3 text-sm text-[#065F46] bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" /> We'll send your report to {email} shortly!
                </div>
                <ThemedButton
                  onClick={() => navigate(getQuotePath())}
                  data-testid="email-saved-get-quote-btn"
                  variant="gradient" size="sm" className="shrink-0"
                >
                  Get a Quote <ChevronRight className="ml-1 h-3 w-3" />
                </ThemedButton>
              </div>
            )}

            {/* ═══ SECTION 1: Safety Practices Assessment ═══ */}
            {questionResults.length > 0 && (
              <Card className="border border-[#E5E7EB] relative overflow-hidden" data-testid="safety-practices-section">
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-1">
                    <ClipboardCheck className="h-5 w-5 text-[#7B6FE8]" />
                    <h3 className="font-black text-[#1A1A1A] uppercase text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      Athlete Safety Practices
                    </h3>
                  </div>
                  <p className="text-sm text-[#6B7280] mb-5">How your organization's safety practices measure up, with specific recommendations.</p>

                  {resultCategories.map((cat) => {
                    const catResults = questionResults.filter(q => q.category === cat);
                    const scoredResults = catResults.filter(q => !q.excluded);
                    const catEarned = scoredResults.reduce((s, q) => s + q.points_earned, 0);
                    const catMax = scoredResults.reduce((s, q) => s + q.points_possible, 0);
                    const allNA = catResults.every(q => q.excluded);
                    return (
                      <div key={cat} className="mb-6 last:mb-0">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-bold text-sm text-[#1A1A1A]">{cat}</h4>
                          {allNA ? (
                            <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280]">N/A</span>
                          ) : (
                            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${catEarned === catMax ? 'bg-[#ECFDF5] text-[#065F46]' : catEarned >= catMax * 0.5 ? 'bg-[#FFFBEB] text-[#92400E]' : 'bg-[#EEF0FF] text-[#7B6FE8]'}`}>
                              {catEarned} / {catMax} pts
                            </span>
                          )}
                        </div>
                        <div className="space-y-2">
                          {catResults.map((qr) => (
                            <div key={qr.id} className={`p-3 rounded-lg border ${qr.excluded ? 'bg-[#F7F8FA] border-[#E5E7EB]' : qr.answered_yes ? 'bg-[#FAFFFE] border-[#A7F3D0]' : 'bg-[#FFFAF7] border-[#FDE68A]'}`} data-testid={`result-${qr.id}`}>
                              <div className="flex items-start gap-2">
                                {qr.excluded ? <Info className="h-4 w-4 text-[#6B7280] mt-0.5 flex-shrink-0" /> : qr.answered_yes ? <CheckCircle2 className="h-4 w-4 text-[#065F46] mt-0.5 flex-shrink-0" /> : <AlertTriangle className="h-4 w-4 text-[#92400E] mt-0.5 flex-shrink-0" />}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#1A1A1A]">{qr.question}</p>
                                  <p className="text-xs text-[#555] mt-1 leading-relaxed">{qr.recommendation}</p>
                                </div>
                                <span className="text-xs font-semibold text-[#6B7280] flex-shrink-0">{qr.excluded ? 'N/A' : qr.answered_yes ? `+${qr.points_earned}` : '0'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* ═══ SECTION 2: Insurance Coverage (from COI) ═══ */}
            {coi.coverages && coi.coverages.length > 0 && (
              <Card className="border border-[#E5E7EB] relative overflow-hidden" data-testid="coverage-section">
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className="h-5 w-5 text-[#7B6FE8]" />
                    <h3 className="font-black text-[#1A1A1A] uppercase text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Your Insurance Coverage</h3>
                  </div>
                  {riskAssessment.transfer_summary && <p className="text-sm text-[#6B7280] mb-5">{riskAssessment.transfer_summary}</p>}
                  <div className="space-y-4">
                    {coi.coverages.map((cov, i) => (
                      <div key={i} className="p-4 bg-[#F7F8FA] rounded-xl border border-[#E5E7EB]" data-testid="coverage-card">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-[#1A1A1A]">{cov.type}</h4>
                              {cov.protection_level && <ProtectionBadge level={cov.protection_level} />}
                            </div>
                            {cov.plain_english && <p className="text-sm text-[#555] mt-1">{cov.plain_english}</p>}
                            {cov.risk_scenario && <p className="text-xs text-[#7B6FE8] mt-2 italic">{cov.risk_scenario}</p>}
                          </div>
                          <div className="text-right flex-shrink-0">
                            {cov.per_occurrence_limit && cov.per_occurrence_limit !== 'Not shown on certificate' && (
                              <div>
                                <p className="text-xs text-[#6B7280]">Per incident</p>
                                <p className="font-semibold text-[#1A1A1A]">{cov.per_occurrence_limit}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        {cov.aggregate_limit && cov.aggregate_limit !== 'Not shown on certificate' && (
                          <div className="mt-2 pt-2 border-t border-[#E5E7EB] flex items-center gap-2 text-xs text-[#6B7280]">
                            <span>Annual maximum: <strong className="text-[#1A1A1A]">{cov.aggregate_limit}</strong></span>
                            {cov.expiration && <span className="ml-auto">Expires: {cov.expiration}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Risk Visibility */}
            {topRisks.length > 0 && (
              <Card className="border border-[#E5E7EB] relative overflow-hidden" data-testid="risk-visibility-section">
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-1">
                    <Eye className="h-5 w-5 text-[#7B6FE8]" />
                    <h3 className="font-black text-[#1A1A1A] uppercase text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Risk Visibility</h3>
                  </div>
                  {riskAssessment.visibility_summary && <p className="text-sm text-[#6B7280] mb-5">{riskAssessment.visibility_summary}</p>}
                  <div className="space-y-3">
                    {topRisks.map((risk, i) => (
                      <div key={i} className="flex items-start gap-3 p-4 bg-[#F7F8FA] rounded-xl border border-[#E5E7EB]" data-testid="top-risk-item">
                        {risk.status === 'Protected' ? <ShieldCheck className="h-5 w-5 text-[#065F46] flex-shrink-0 mt-0.5" /> : risk.status === 'Partially Protected' ? <AlertTriangle className="h-5 w-5 text-[#92400E] flex-shrink-0 mt-0.5" /> : <Sparkles className="h-5 w-5 text-[#7B6FE8] flex-shrink-0 mt-0.5" />}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm text-[#1A1A1A]">{risk.risk}</span>
                            <StatusBadge status={risk.status} />
                          </div>
                          <p className="text-sm text-[#555] leading-relaxed">{risk.what_to_do}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Mitigation Tips */}
            {mitigationTips.length > 0 && (
              <Card className="border border-[#E5E7EB] relative overflow-hidden" data-testid="mitigation-section">
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#4ECDC4] to-[#6B5CE7]"></div>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-1">
                    <Wrench className="h-5 w-5 text-[#4ECDC4]" />
                    <h3 className="font-black text-[#1A1A1A] uppercase text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Tools & Resources to Reduce Your Risk</h3>
                  </div>
                  <p className="text-sm text-[#6B7280] mb-5">Practical steps you can take today — no insurance needed.</p>
                  <div className="space-y-3">
                    {mitigationTips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-3 p-4 bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl" data-testid="mitigation-tip">
                        <CheckCircle2 className="h-5 w-5 text-[#065F46] flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-[#1A1A1A] leading-relaxed">{tip}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Highlights */}
            {highlights.length > 0 && (
              <div data-testid="highlights-section">
                <h3 className="font-black text-[#1A1A1A] uppercase text-lg mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Things Worth Knowing</h3>
                <div className="space-y-3">{highlights.map((h, i) => <HighlightCard key={i} highlight={h} />)}</div>
              </div>
            )}

            {/* Gap Analysis */}
            {analysis.gap_analysis && analysis.gap_analysis.length > 0 && (
              <Card className="border border-[#E5E7EB] relative overflow-hidden" data-testid="gap-analysis-section">
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#E040FB] via-[#FF6B35] to-[#FFD93D]"></div>
                <CardContent className="pt-6">
                  <h3 className="font-black text-[#1A1A1A] uppercase text-lg mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>How You Stack Up</h3>
                  <p className="text-sm text-[#6B7280] mb-4">We compared your certificate against the requirements you uploaded.</p>
                  <div className="space-y-3">
                    {analysis.gap_analysis.map((gap, i) => (
                      <div key={i} className="flex items-start gap-3 p-4 bg-[#F7F8FA] rounded-xl border border-[#E5E7EB]">
                        {gap.status === 'MET' ? <CheckCircle2 className="h-5 w-5 text-[#065F46] flex-shrink-0" /> : gap.status === 'GAP' ? <AlertTriangle className="h-5 w-5 text-[#92400E] flex-shrink-0" /> : <Sparkles className="h-5 w-5 text-[#7B6FE8] flex-shrink-0" />}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm text-[#1A1A1A]">{gap.coverage_type}</span>
                            <StatusBadge status={gap.status} />
                          </div>
                          <p className="text-sm text-[#555]">{gap.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA */}
            <Card className="bg-[#232323] border-0 text-white overflow-hidden relative" data-testid="cta-section">
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#E040FB] via-[#FF6B35] to-[#FFD93D]"></div>
              <CardContent className="pt-8 pb-8 text-center">
                <Users className="h-8 w-8 text-[#4ECDC4] mx-auto mb-4" />
                <h3 className="font-black uppercase text-xl mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Let Us Help You Close the Gaps</h3>
                <p className="text-white/70 text-sm mb-6 max-w-md mx-auto leading-relaxed">
                  Athletes, admins, and parents who love what sports bring to the world. All we do is sports and fitness — we've seen it all. Start your application now and your report data will be pre-filled.
                </p>
                <ThemedButton
                  onClick={() => navigate(getQuotePath())}
                  data-testid="get-quote-from-results-btn"
                  variant="gradient" size="lg"
                >
                  Get a Personalized Quote <ChevronRight className="ml-1 h-4 w-4" />
                </ThemedButton>
              </CardContent>
            </Card>

            {/* E&O Disclaimer */}
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4 mt-2" data-testid="eo-disclaimer">
              <p className="text-[11px] text-[#6B7280] leading-relaxed">
                <strong>Disclaimer:</strong> This risk assessment report is provided for informational and educational purposes only and does not constitute insurance advice, a coverage opinion, legal advice, or a binding offer of insurance. The information contained herein is based on data you provided and automated analysis — it may not reflect the complete terms, conditions, exclusions, or limitations of any insurance policy. Players Health makes no representations or warranties, express or implied, regarding the accuracy, completeness, or adequacy of any information in this report. You should consult with a licensed insurance professional or broker to obtain advice specific to your organization's needs and circumstances. Players Health shall not be liable for any errors, omissions, or for any actions taken in reliance on the information provided in this report.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskAssessment;
